import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
// @ts-expect-error TODO untyped
import SimpleLineIcons from 'react-simple-line-icons';
import PulseLoader from 'react-spinners/PulseLoader';
import { Badge, Button, Col, Modal, ModalBody, Row } from 'reactstrap';

import classNames from '../../helpers/classNames';
import { formatDateTime } from '../../helpers/datetime';
import { formatCurrency } from '../../helpers/formatCurrency';
import useTranslate from '../../hooks/useTranslate';
import { MappedGolfCourseType } from '../../services/apis/golfCourses/types';
import {
  ExtraProductsType,
  TeeTimeExtraProductPrebookType,
  TeeTimePrebookMappedType,
  TeeTimeRatePrebookMappedType,
} from '../../services/apis/teeTimes/types';

const extractExtraProducts = (
  extraProducts?: ExtraProductsType | null
): TeeTimeExtraProductPrebookType[] | null => {
  if (extraProducts) {
    const items = (
      (Object.keys(extraProducts) as Array<keyof typeof extraProducts>).filter(
        type => type !== 'id' && extraProducts[type]
      ) as Array<TeeTimeExtraProductPrebookType['type']>
    ).map(type => ({
      // Convert type to name by capitalize first letter and add space before eny other capitalized letter
      name: (type.charAt(0).toUpperCase() + type.slice(1)).replace(/([A-Z])/g, ' $1').trim(),
      type,
      price: extraProducts[type] || 0,
      id: extraProducts.id,
      count: 0,
    }));

    return items;
  }

  return null;
};

interface RateItemProps {
  rate: TeeTimeRatePrebookMappedType;
  currency: string;
  disabledPlus: boolean;
  onPressPlus: () => void;
  onPressMinus: () => void;
  showCampaignAlert?: boolean;
}

const RateItem: FC<RateItemProps> = props => {
  const { rate, currency, disabledPlus, onPressPlus, onPressMinus, showCampaignAlert } = props;
  const { name, price, rackPrice, discount, nrPlayers, amount } = rate;
  const { activeLanguage } = useTranslate();
  const disabledMinus = nrPlayers === 0;
  const { translate } = useTranslate();

  return (
    <div className="rate-item">
      <div className="d-flex justify-content-between">
        <div className="d-flex flex-column align-items-start" style={{ gap: '8px' }}>
          <div className="fw-bold">{name}</div>

          {discount > 0 && price < rackPrice && (
            <Badge color="danger" pill className="fs-6">
              {Number((-1 * discount) / 100).toLocaleString(undefined, {
                style: 'percent',
              })}
            </Badge>
          )}
          <div>
            <div className="d-flex gap-1 lead">
              <strong
                className={classNames(
                  'fw-bold',
                  discount > 0 && !!rackPrice ? 'text-danger' : undefined
                )}
              >
                {formatCurrency(currency, price, activeLanguage.code)}
              </strong>

              {discount > 0 && price < rackPrice && (
                <s>{formatCurrency(currency, rackPrice, activeLanguage.code)}</s>
              )}
            </div>

            {showCampaignAlert && (
              <p className="text-muted mb-0 mt-2 small fst-italic">
                {rate.useCampaign
                  ? translate('discount-campaign.alerts.rates-modal-campaign')
                  : translate('discount-campaign.alerts.rates-modal')}
              </p>
            )}
          </div>
        </div>

        <div className="d-flex mt-sm-0 align-items-center justify-content-end justify-content-sm-around gap-2 pe-2">
          <Button
            color="outline"
            onClick={onPressMinus}
            disabled={disabledMinus}
            className="p-0 rounded-circle"
          >
            <SimpleLineIcons
              size="Large"
              name="minus"
              style={{ opacity: disabledMinus ? 0.4 : 1 }}
            />
          </Button>
          <span className="quantatity">{amount || 0}</span>
          <Button
            color="outline"
            onClick={onPressPlus}
            disabled={disabledPlus}
            className="p-0 rounded-circle"
          >
            <SimpleLineIcons size="Large" name="plus" style={{ opacity: disabledPlus ? 0.4 : 1 }} />
          </Button>
        </div>
      </div>
    </div>
  );
};

interface ExtraProductItemProps {
  extraProduct: TeeTimeExtraProductPrebookType;
  currency: string;
  disabledPlus: boolean;
  onPressPlus: () => void;
  onPressMinus: () => void;
}

const ExtraProductItem: FC<ExtraProductItemProps> = props => {
  const { extraProduct, currency, disabledPlus, onPressPlus, onPressMinus } = props;
  const { price, count, type } = extraProduct;
  const { activeLanguage } = useTranslate();

  const { translate } = useTranslate();
  const disabledMinus = count === 0;

  return (
    <div className="rate-item">
      <div className="d-flex justify-content-between">
        <div className="d-flex flex-column gap-2 align-items-start">
          <div className="fw-bold">{translate(`bookingExtraProducts.${type}`)}</div>

          <div className="d-flex gap-1 lead">
            <strong className="fw-bold">
              {formatCurrency(currency, price, activeLanguage.code)}
            </strong>
          </div>
        </div>

        <div className="d-flex align-items-center justify-content-around gap-2 pe-2">
          <Button
            color="ouline"
            onClick={onPressMinus}
            disabled={disabledMinus}
            className="p-0 rounded-circle"
          >
            <SimpleLineIcons
              size="Large"
              name="minus"
              style={{ opacity: disabledMinus ? 0.4 : 1 }}
            />
          </Button>
          <span className="quantatity">{count || 0}</span>
          <Button
            color="ouline"
            onClick={onPressPlus}
            disabled={disabledPlus}
            className="p-0 rounded-circle"
          >
            <SimpleLineIcons size="Large" name="plus" style={{ opacity: disabledPlus ? 0.4 : 1 }} />
          </Button>
        </div>
      </div>
    </div>
  );
};

interface RatesModalProps {
  teetime: TeeTimePrebookMappedType; // fixme refactor to TeeTimeType and currency & extraProducts additional properties
  course: MappedGolfCourseType;
  error?: string;
  inProgress?: boolean;
  prebookBooking: (
    selectedRates: TeeTimeRatePrebookMappedType[],
    selectedExtraProducts: TeeTimeExtraProductPrebookType[],
    goToCheckout?: boolean
  ) => void;
  onClose?: () => void;
  skipCheckout?: boolean;
  informer?: ReactNode;
  disabled?: boolean;
}

const RatesModal: FC<RatesModalProps> = props => {
  const {
    prebookBooking,
    onClose,
    teetime,
    course,
    inProgress,
    error,
    skipCheckout = false,
    informer,
    disabled = false,
  } = props;
  const { translate, activeLanguage } = useTranslate();
  const [selectedRates, setSelectedRates] = useState<TeeTimeRatePrebookMappedType[]>([]);
  const [nrOfSelectedPlayers, setNrOfSelectedPlayers] = useState(0);
  const [selectedExtraProducts, setSelectedExtraProducts] = useState<
    TeeTimeExtraProductPrebookType[]
  >([]);

  const showCampaignAlert = useMemo(
    () => teetime.rates?.some(rate => rate.useCampaign),
    [teetime.rates]
  );

  const { dateTime, rates, currency, availablePlayers, extraProducts } = teetime;
  useEffect(() => {
    if (rates?.length) {
      setSelectedRates(rates.map(r => ({ ...r, amount: 0, nrPlayers: 0 })));
    } else {
      setSelectedRates([
        // @ts-expect-error TODO untyped
        {
          name: String(translate('default.ChooseNrPlayers')),
          amount: 0,
          nrPlayers: 0,
          requiredPlayers: 1,
        },
      ]);
    }
    const newExtraProducts = extractExtraProducts(extraProducts);

    if (newExtraProducts) {
      setSelectedExtraProducts(newExtraProducts);
    }
  }, [rates, extraProducts, translate]);

  const addRate = (rate: TeeTimeRatePrebookMappedType) => {
    setSelectedRates(prevState =>
      prevState.map(r =>
        r.name === rate.name
          ? { ...r, amount: r.amount + 1, nrPlayers: r.nrPlayers + r.requiredPlayers }
          : { ...r }
      )
    );

    setNrOfSelectedPlayers(prevState => prevState + 1 * rate.requiredPlayers);
  };

  const removeRate = (rate: TeeTimeRatePrebookMappedType) => {
    setSelectedRates(prevState =>
      prevState.map(r =>
        r.name === rate.name
          ? { ...r, amount: r.amount - 1, nrPlayers: r.nrPlayers - r.requiredPlayers }
          : { ...r }
      )
    );

    setNrOfSelectedPlayers(prevState => prevState - 1 * rate.requiredPlayers);
  };

  const addExtraProduct = (xp: TeeTimeExtraProductPrebookType) => {
    setSelectedExtraProducts(prevState =>
      prevState.map(r => (r.name === xp.name ? { ...r, count: r.count + 1 } : { ...r }))
    );
  };

  const removeExtraProduct = (xp: TeeTimeExtraProductPrebookType) => {
    setSelectedExtraProducts(prevState =>
      prevState.map(r => (r.name === xp.name ? { ...r, count: r.count - 1 } : { ...r }))
    );
  };

  const fullFlight = nrOfSelectedPlayers === availablePlayers;
  const buttonsDisabled = disabled || inProgress || nrOfSelectedPlayers === 0;

  // Check if a rate with the same RateId but different campaign status has been selected
  const isCounterpartRateSelected = (rate: TeeTimeRatePrebookMappedType) => {
    if (!rate.specificData || !('RateId' in rate.specificData)) return false;

    const rateId = rate.specificData.RateId;

    return selectedRates.some(r => r.useCampaign !== rate.useCampaign && r.amount > 0);
  };

  return (
    <div>
      <Modal centered isOpen toggle={onClose}>
        <div className="p-3">
          <div className="text-end">
            <SimpleLineIcons
              name="close"
              size="Large"
              role="button"
              onClick={onClose}
              style={{ cursor: 'pointer' }}
            />
          </div>

          <ModalBody>
            <div className="pb-3 px-sm-5 text-center">
              <h2 className="text-ontee-red fw-bold">{course.golfClub.name.toUpperCase()}</h2>
              <h3 className="fw-bold">{course.golfCourse.name.toUpperCase()}</h3>
              <h4 className="fw-bold">{formatDateTime(dateTime, activeLanguage.code)}</h4>
            </div>
            {informer}
            {/* <AlertNotifications /> */}
            {selectedRates.map(rate => (
              <RateItem
                key={rate.name + rate.price}
                rate={rate}
                currency={currency}
                disabledPlus={
                  fullFlight ||
                  rate.requiredPlayers + nrOfSelectedPlayers > availablePlayers ||
                  isCounterpartRateSelected(rate)
                }
                onPressPlus={() => addRate(rate)}
                onPressMinus={() => removeRate(rate)}
                showCampaignAlert={showCampaignAlert}
              />
            ))}
            {selectedExtraProducts?.map(extraProduct => (
              <ExtraProductItem
                key={extraProduct.name}
                extraProduct={extraProduct}
                currency={currency}
                disabledPlus={extraProduct.count === availablePlayers}
                onPressPlus={() => addExtraProduct(extraProduct)}
                onPressMinus={() => removeExtraProduct(extraProduct)}
              />
            ))}
            <Row>
              <Col className="text-end mt-3">
                {inProgress && (
                  <div>
                    <PulseLoader size={10} />
                  </div>
                )}
                {error && <p className="alert alert-danger">{error}</p>}

                <Button
                  color="success"
                  onClick={() => prebookBooking(selectedRates, selectedExtraProducts)}
                  disabled={buttonsDisabled}
                >
                  {translate('default.AddToCart')}
                </Button>

                {!skipCheckout && (
                  <Button
                    color="success"
                    className="ms-1"
                    disabled={buttonsDisabled}
                    onClick={() => prebookBooking(selectedRates, selectedExtraProducts, true)}
                  >
                    {translate('BookingContainer.GoToCheckout')}
                  </Button>
                )}
              </Col>
            </Row>
          </ModalBody>
        </div>
      </Modal>
    </div>
  );
};

export default RatesModal;
